import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import get from "lodash/get"
import clsx from "clsx"
import { Avatar, Chip, Typography, useTheme } from "@mui/material"

import { getIcon } from "utils/icon"
import {
  getCategoryIcon,
  getFeatureDetail,
  IOccupantDetail,
} from "utils/feature"
import { FeatureListItem } from "components/domains/features"
import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"
import { ErrorRoundedIcon, ShopIcon } from "../../icons"
import { OccupantData, ImdfFeature } from "providers/venue/types"
import { ComingSoonChip, NewShopChip } from "components/chips"

const DEFAULT_SEPARATOR = " | "

interface IShopListItemProps {
  occupant: OccupantData
  hideLevel?: boolean
}

export const ShopListItem = ({
  occupant,
  hideLevel = false,
  ...props
}: IShopListItemProps) => {
  const theme = useTheme()

  const { t } = useTranslation({ defaultValue: null })

  const { isMultiVenueProject } = useVenue()
  const navigate = useNavigate()

  const styleOverrides =
    theme.components[`FeatureListItem`]?.styleOverrides || {}

  const {
    boothNameText,
    locationText,
    categoryText,
    venueText,
    maintenanceIcon,
    sponsoredChip,
    comingSoonChip,
    newShopChip,
  } = styleOverrides

  const defaultIconProps = {
    imageIconProps: {
      solidColorFilterId: "diningCard_cover_filter",
    },
  }

  const {
    boothNameTextSeparator = DEFAULT_SEPARATOR,
    locationTextSeparator = DEFAULT_SEPARATOR,
    // TODO: move `showStatusChip` config from app config
    showStatusChip = false,
    imageIconProps = defaultIconProps,
  } = theme.components[`FeatureListItem`]?.defaultProps || {}

  const {
    name,
    logo,
    level,
    group,
    zone,
    venueLogo,
    boothName,
    venueRef,
    category,
    maintenance,
    isAd,
    venueName,
    hasMultipleLocations,
    cardStyle,
  } = useMemo(
    () => getFeatureDetail(occupant as ImdfFeature),
    [occupant]
  ) as IOccupantDetail

  const { isNewShop, isComingSoon } = occupant?.properties

  const handleClick = useCallback(
    () => navigate(`/maps/place/${occupant.id}`),
    [navigate, occupant?.id]
  )

  const zoneText = useMemo(() => t(zone), [t, zone])

  const categoryIcon = useMemo(() => {
    const parentCategory = get(occupant, "properties.local_categories[0]", {})

    return getCategoryIcon(parentCategory as ImdfFeature)
  }, [occupant])

  const groupText = useMemo(() => t(group), [t, group])

  return (
    <FeatureListItem
      divider
      className={clsx(venueRef, cardStyle)}
      onClick={handleClick}
      startAdornment={
        showStatusChip && (
          <>
            {isAd && (
              <Chip
                className="FeatureListItem-sponsoredChip"
                label={t("sponsored.search_result")}
                sx={sponsoredChip}
              />
            )}
            {isComingSoon && !isAd && (
              <ComingSoonChip
                className="FeatureListItem-comingSoonChip"
                sx={comingSoonChip}
                label={t("Coming soon")}
              />
            )}
            {isNewShop && !isAd && (
              <NewShopChip
                className="FeatureListItem-newShopChip"
                sx={newShopChip}
              />
            )}
          </>
        )
      }
      primary={
        <>
          {maintenance?.enable ? (
            <ErrorRoundedIcon
              color={maintenance?.hasTempLocation ? "warning" : "error"}
              sx={maintenanceIcon}
            />
          ) : null}
          {t(name)}
        </>
      }
      secondary={
        hasMultipleLocations ? (
          <Typography component="span">{t("Multiple Locations")}</Typography>
        ) : (
          <>
            {/* // TODO: Refactor secondary text display to allow configuration via theme. */}
            <Typography component="span" sx={boothNameText}>
              {_.compact([
                boothName && `${t("Booth")} ${t(boothName)} `,
                boothName &&
                  ((!hideLevel &&
                    `${t("At Floor", {
                      value: level,
                    })}`) ||
                    zoneText),
              ]).join(boothNameTextSeparator)}
            </Typography>
            <Typography component="span" sx={locationText}>
              {_.compact([
                !hideLevel &&
                  level &&
                  `${t("At Floor", {
                    value: level,
                  })} `,
                !hideLevel && level && zoneText,
                zoneText && `${t("Zone")} ${zoneText} `,
                group && groupText,
              ]).join(locationTextSeparator)}
            </Typography>
            {category && (
              <Typography component="span" sx={categoryText}>
                {t(category)}
              </Typography>
            )}
            {venueText && (
              <Typography component="span" sx={venueText}>
                {t(venueName ?? "")}
              </Typography>
            )}
          </>
        )
      }
      avatar={
        <Avatar
          src={logo}
          variant="logo"
          className={clsx("occupant-logo-avatar", { "no-logo": !logo })}
        >
          {!logo &&
            (getIcon(categoryIcon, null, imageIconProps) || <ShopIcon />)}
        </Avatar>
      }
      endAdornmentSlotClasses={{
        text: venueRef,
      }}
      endAdornmentSlots={{
        text: t(level),
        media: !hasMultipleLocations && isMultiVenueProject && venueLogo,
      }}
      {...props}
    />
  )
}

export default ShopListItem
